import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { MessageCircle, Printer, ReceiptText, RotateCcw, Search, Undo2, XCircle } from 'lucide-react';
import { ORDER_SOURCES, ORDER_TYPES, REFUND_MODES } from '@shripos/shared/constants';
import {
  Alert,
  Badge,
  Button,
  Card,
  DescriptionList,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Select,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Textarea,
  formatDateTime,
  useToast,
} from '@shripos/ui';
import { ReceiptView } from '../../components/ReceiptView.jsx';
import { api, qs } from '../../lib/api.js';
import { rupees } from '../../lib/money.js';
import { withOverride } from '../../lib/override.js';
import { useCart } from '../../stores/cart.js';
import { can, kitchenOf, useSession } from '../../stores/session.js';
import { usePrinting } from '../../lib/printing/usePrinting.js';
import { EmailReceipt, waLink } from '../pos/ReceiptActions.jsx';
import { ReasonDialog } from '../pos/SmallDialogs.jsx';

const STATUS_TONE = {
  completed: 'success',
  held: 'info',
  cancelled: 'neutral',
  voided: 'danger',
  partially_refunded: 'warning',
  refunded: 'warning',
};
const STATUS_LABEL = {
  completed: 'Paid',
  held: 'Open',
  cancelled: 'Cancelled',
  voided: 'Void',
  partially_refunded: 'Part refunded',
  refunded: 'Refunded',
};
const typeLabel = (k, t) => t(`pos.type.${k}`, ORDER_TYPES.find((o) => o.key === k)?.label ?? k);
const sourceLabel = (k, t) => t(`pos.source.${k}`, ORDER_SOURCES.find((o) => o.key === k)?.label ?? k);
const todayIso = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date(Date.now() - 4 * 3_600_000));

export function StatusBadge({ status }) {
  const { t } = useTranslation();
  return <Badge tone={STATUS_TONE[status]}>{t(`bills.status.${status}`, STATUS_LABEL[status] ?? status)}</Badge>;
}

function RefundDialog({ bill, onDone, onClose }) {
  const { t } = useTranslation();
  const toast = useToast();
  const refundable = bill.lines.filter((l) => l.qty - l.refundedQty > 0);
  const [qty, setQty] = useState(() => Object.fromEntries(refundable.map((l) => [l.lineId, 0])));
  const [mode, setMode] = useState(
    bill.payments.length === 1 && bill.payments[0].mode !== 'due' ? bill.payments[0].mode : 'cash',
  );
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const chosen = Object.entries(qty).filter(([, q]) => q > 0);
  const full = refundable.every((l) => qty[l.lineId] === l.qty - l.refundedQty);
  const submit = async () => {
    setBusy(true);
    try {
      const body = full
        ? { reason, full: true, refundMode: mode }
        : { reason, lines: chosen.map(([lineId, q]) => ({ lineId, qty: q })), refundMode: mode };
      const res = await withOverride((headers) => api.post(`/bills/${bill.id}/refund`, body, { headers }));
      toast.success(
        t('bills.refunded', 'Credit note {{number}} · refund {{amount}}', {
          number: res.creditNote.number,
          amount: rupees(res.creditNote.totals.totalPaise),
        }),
      );
      onDone();
      onClose();
    } catch (err) {
      if (!err.cancelled) toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`${t('bills.refund', 'Refund')} · ${bill.invoiceNo}`}
      description={t('bills.refundHint', 'Issues a GST credit note. Choose the items being returned.')}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => setQty(Object.fromEntries(refundable.map((l) => [l.lineId, l.qty - l.refundedQty])))}
          >
            {t('bills.selectAll', 'Select everything')}
          </Button>
          <Button
            variant="danger"
            disabled={!chosen.length || reason.trim().length < 3}
            loading={busy}
            onClick={submit}
          >
            {t('bills.refund', 'Refund')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ul className="divide-y divide-slate-100 rounded-lg ring-1 ring-slate-200">
          {refundable.map((l) => {
            const max = l.qty - l.refundedQty;
            return (
              <li key={l.lineId} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="min-w-0 text-sm">
                  <span className="block truncate font-medium">
                    {l.name}
                    {l.variantName ? ` (${l.variantName})` : ''}
                  </span>
                  <span className="text-xs text-slate-500">
                    {t('bills.refundable', '{{max}} of {{qty}} refundable', { max, qty: l.qty })}
                  </span>
                </span>
                <Select
                  value={qty[l.lineId]}
                  onChange={(e) => setQty({ ...qty, [l.lineId]: Number(e.target.value) })}
                  className="w-20"
                >
                  {Array.from({ length: max + 1 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </Select>
              </li>
            );
          })}
        </ul>
        {full && (
          <Alert tone="info">{t('bills.fullRefund', 'Full refund: charges and round-off are refunded too.')}</Alert>
        )}
        <Field label={t('bills.refundBy', 'Refund paid by')}>
          <Select value={mode} onChange={(e) => setMode(e.target.value)}>
            {REFUND_MODES.filter((m) => !['due_adjust', 'points'].includes(m) || bill.customer?.id).map((m) => (
              <option key={m} value={m}>
                {m === 'due_adjust'
                  ? t('bills.reduceDue', 'Reduce customer due')
                  : m === 'points'
                    ? t('bills.refundPoints', 'Loyalty points (₹1 = 1 point by default)')
                    : t(`pos.mode.${m}`, m.toUpperCase())}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('pos.reasonRequired', 'Reason (required)')}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value.slice(0, 300))} />
        </Field>
      </div>
    </Dialog>
  );
}

export function BillDetailDialog({ billId, onClose }) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useSession((s) => s.me);
  const loadBill = useCart((s) => s.loadBill);
  const cart = useCart((s) => s.cart);
  const printing = usePrinting();
  const [sub, setSub] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const bill = useQuery({ queryKey: ['bill', billId], queryFn: () => api.get(`/bills/${billId}`) });
  const receipt = useQuery({
    queryKey: ['receipt', billId],
    queryFn: () => api.get(`/bills/${billId}/receipt`),
    enabled: showReceipt,
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bill', billId] });
    qc.invalidateQueries({ queryKey: ['receipt', billId] });
    qc.invalidateQueries({ queryKey: ['bills'] });
  };
  const b = bill.data;
  const closed = b && ['completed', 'voided', 'partially_refunded', 'refunded'].includes(b.status);

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={
        b
          ? (b.invoiceNo ?? t('bills.openOrderToken', 'Open order · Token {{token}}', { token: b.tokenNo ?? '—' }))
          : t('bills.bill', 'Bill')
      }
    >
      {!b ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={b.status} />
            <span className="text-sm text-slate-500">
              {typeLabel(b.orderType, t)} · {sourceLabel(b.source, t)} · {formatDateTime(b.invoiceDate ?? b.createdAt)}
            </span>
          </div>
          {b.void && (
            <Alert tone="danger">
              {t('bills.voidedBy', 'Voided by {{name}}', { name: b.void.by })}
              {b.void.approvedBy
                ? ` ${t('bills.approvedBy', '(approved by {{name}})', { name: b.void.approvedBy })}`
                : ''}
              : {b.void.reason}
            </Alert>
          )}
          {b.cancel && (
            <Alert tone="warning">
              {t('bills.cancelledBy', 'Cancelled by {{name}}', { name: b.cancel.by })}: {b.cancel.reason}
            </Alert>
          )}
          <DescriptionList
            items={[
              [t('pos.token', 'Token'), b.tokenNo ?? '—'],
              [t('pos.table', 'Table'), b.tableNo],
              [
                t('pos.customer', 'Customer'),
                b.customer ? `${b.customer.name ?? ''} ${b.customer.phone ?? ''}`.trim() : null,
              ],
              [t('bills.billedBy', 'Billed by'), b.completedBy?.name ?? b.createdBy?.name],
              [
                t('bills.payments', 'Payments'),
                b.payments.map((p) => `${p.mode.toUpperCase()} ${rupees(p.amountPaise)}`).join(' + ') || null,
              ],
              b.overrides.length > 0 && [
                t('bills.approvals', 'Approvals'),
                b.overrides
                  .map((o) =>
                    t('bills.approvalBy', '{{permission}} by {{name}}', {
                      permission: o.permission,
                      name: o.managerName,
                    }),
                  )
                  .join(', '),
              ],
            ]}
          />
          <Table>
            <THead>
              <TR>
                <TH>{t('pos.item', 'Item')}</TH>
                <TH className="text-right">{t('bills.qty', 'Qty')}</TH>
                <TH className="text-right">{t('bills.amount', 'Amount')}</TH>
              </TR>
            </THead>
            <TBody>
              {b.lines.map((l) => (
                <TR key={l.lineId}>
                  <TD>
                    {l.name}
                    {l.variantName ? ` (${l.variantName})` : ''}
                    {l.modifiers.length > 0 && (
                      <span className="block text-xs text-slate-500">
                        + {l.modifiers.map((m) => m.name).join(', ')}
                      </span>
                    )}
                    {l.refundedQty > 0 && (
                      <span className="block text-xs text-amber-700">
                        {t('bills.refundedQty', '{{count}} refunded', { count: l.refundedQty })}
                      </span>
                    )}
                  </TD>
                  <TD className="text-right tabular-nums">{l.qty}</TD>
                  <TD className="text-right tabular-nums">
                    {rupees(l.amounts.grossPaise - l.amounts.lineDiscountPaise)}
                  </TD>
                </TR>
              ))}
              <TR>
                <TD className="font-semibold">{t('so.total', 'Total')}</TD>
                <TD />
                <TD className="text-right font-semibold tabular-nums">{rupees(b.totals.grandTotalPaise)}</TD>
              </TR>
            </TBody>
          </Table>
          {b.creditNotes?.length > 0 && (
            <div>
              <p className="mb-1 text-sm font-semibold">{t('bills.creditNotes', 'Credit notes')}</p>
              <ul className="text-sm text-slate-600">
                {b.creditNotes.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-2">
                    {c.number} · {rupees(c.totals.totalPaise)} · {c.refundMode} · {c.reason} · {formatDateTime(c.date)}
                    <Button size="xs" variant="ghost" icon={Printer} onClick={() => printing.creditNote(b.id, c.id)}>
                      {t('pos.print', 'Print')}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {b.kots?.length > 0 && (
            <p className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              {t('bills.kots', 'KOTs')}:{' '}
              {b.kots
                .map(
                  (k) =>
                    `#${k.kotNo} ${k.station}${k.type === 'cancel' ? ` ${t('bills.cancelShort', '(cancel)')}` : ''}`,
                )
                .join(', ')}
              {/* Tickets from before the kitchen was switched off stay listed, but don't reprint. */}
              {kitchenOf(me).orders && (
                <Button size="xs" variant="ghost" icon={Printer} onClick={() => printing.kots(b.kots)}>
                  {t('bills.reprintKots', 'Reprint KOTs')}
                </Button>
              )}
            </p>
          )}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            {b.status === 'held' && (
              <Button
                icon={RotateCcw}
                onClick={() => {
                  if (cart.lines.length && cart.billId !== b.id)
                    return toast.error(t('bills.holdFirst', 'Hold or clear the order on the billing screen first.'));
                  loadBill(b);
                  navigate('/pos');
                }}
              >
                {t('bills.openInBilling', 'Open in billing')}
              </Button>
            )}
            {(closed || b.status === 'held') && (
              <Button variant="secondary" icon={Printer} onClick={() => printing.receipt(b.id).then(refresh)}>
                {b.status === 'held'
                  ? t('bills.preBill', 'Print pre-bill')
                  : b.lastPrintedAt
                    ? t('bills.reprint', 'Reprint')
                    : t('pos.print', 'Print')}
              </Button>
            )}
            {closed && (
              <a href={waLink(b)} target="_blank" rel="noreferrer">
                <Button variant="secondary" icon={MessageCircle}>
                  WhatsApp
                </Button>
              </a>
            )}
            {closed && <EmailReceipt bill={b} />}
            <Button variant="ghost" icon={ReceiptText} onClick={() => setShowReceipt((v) => !v)}>
              {showReceipt ? t('bills.hideReceipt', 'Hide receipt') : t('bills.showReceipt', 'Show receipt')}
            </Button>
            {b.status === 'completed' && can(me, 'pos.bill') && (
              <Button variant="danger-outline" icon={XCircle} onClick={() => setSub('void')}>
                {t('bills.void', 'Void')}
              </Button>
            )}
            {['completed', 'partially_refunded'].includes(b.status) && (
              <Button variant="danger-outline" icon={Undo2} onClick={() => setSub('refund')}>
                {t('bills.refund', 'Refund')}
              </Button>
            )}
          </div>
          {showReceipt && (
            <div className="rounded-lg bg-slate-100 p-3">
              {receipt.data ? <ReceiptView model={receipt.data} className="shadow" /> : <Spinner />}
            </div>
          )}
        </div>
      )}
      {sub === 'void' && (
        <ReasonDialog
          title={t('bills.voidTitle', 'Void {{number}}?', { number: b.invoiceNo })}
          description={t(
            'bills.voidHint',
            "Cancels today's bill and reverses its sales. Earlier bills must be refunded instead.",
          )}
          confirmLabel={t('bills.voidBill', 'Void bill')}
          onConfirm={async (reason) => {
            await withOverride((headers) => api.post(`/bills/${b.id}/void`, { reason }, { headers }));
            toast.success(t('bills.voided', 'Bill voided'));
            refresh();
          }}
          onClose={() => setSub(null)}
        />
      )}
      {sub === 'refund' && <RefundDialog bill={b} onDone={refresh} onClose={() => setSub(null)} />}
    </Dialog>
  );
}

export function BillsPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const list = useQuery({
    queryKey: ['bills', { date, status, q, page }],
    queryFn: () => api.get(`/bills${qs({ from: q ? '' : date, to: q ? '' : date, status, q, page, limit: 30 })}`),
    placeholderData: keepPreviousData,
  });
  const set = (fn) => (v) => {
    fn(v);
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title={t('bills.title', 'Bills')}
        description={t('bills.subtitle', 'Every order at this outlet. Search ignores the date.')}
      />
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input
            type="date"
            value={date}
            onChange={(e) => set(setDate)(e.target.value)}
            className="w-auto"
            aria-label={t('cash.businessDay', 'Business day')}
          />
          <Select
            value={status}
            onChange={(e) => set(setStatus)(e.target.value)}
            className="w-auto"
            aria-label={t('sync.status', 'Status')}
          >
            <option value="">{t('bills.all', 'All')}</option>
            <option value="completed">{t('bills.status.completed', 'Paid')}</option>
            <option value="held">{t('bills.status.held', 'Open')}</option>
            <option value="voided">{t('bills.voidedFilter', 'Voided')}</option>
            <option value="partially_refunded,refunded">{t('bills.status.refunded', 'Refunded')}</option>
            <option value="cancelled">{t('bills.status.cancelled', 'Cancelled')}</option>
          </Select>
          <div className="relative min-w-48 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => set(setQ)(e.target.value)}
              placeholder={t('bills.search', 'Bill no., token or phone')}
              className="pl-9"
            />
          </div>
        </div>
        {list.isLoading ? (
          <div className="flex justify-center p-8">
            <Spinner />
          </div>
        ) : list.data.items.length === 0 ? (
          <EmptyState icon={ReceiptText} title={t('bills.none', 'No bills')} />
        ) : (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>{t('bills.bill', 'Bill')}</TH>
                  <TH>{t('bills.time', 'Time')}</TH>
                  <TH>{t('bills.type', 'Type')}</TH>
                  <TH>{t('pos.customer', 'Customer')}</TH>
                  <TH>{t('sync.status', 'Status')}</TH>
                  <TH className="text-right">{t('so.total', 'Total')}</TH>
                </TR>
              </THead>
              <TBody>
                {list.data.items.map((b) => (
                  <TR key={b.id} onClick={() => setOpen(b.id)}>
                    <TD>
                      <p className="font-medium text-slate-900">{b.invoiceNo ?? '—'}</p>
                      <p className="text-xs text-slate-500">
                        {t('pos.token', 'Token')} {b.tokenNo ?? '—'}
                        {b.tableNo ? ` · ${t('pos.table', 'Table')} ${b.tableNo}` : ''}
                      </p>
                    </TD>
                    <TD className="whitespace-nowrap">{formatDateTime(b.invoiceDate ?? b.createdAt)}</TD>
                    <TD>
                      {typeLabel(b.orderType, t)}
                      {b.source !== 'counter' && (
                        <span className="block text-xs text-slate-500">{sourceLabel(b.source, t)}</span>
                      )}
                    </TD>
                    <TD className="text-xs">
                      {b.customer ? `${b.customer.name ?? ''} ${b.customer.phone ?? ''}` : '—'}
                    </TD>
                    <TD>
                      <StatusBadge status={b.status} />
                      {b.paymentModes.length > 0 && (
                        <span className="ml-1 text-xs text-slate-500 uppercase">{b.paymentModes.join('+')}</span>
                      )}
                    </TD>
                    <TD className="text-right font-semibold tabular-nums">{rupees(b.grandTotalPaise)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <Pagination page={list.data.page} limit={list.data.limit} total={list.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      {open && <BillDetailDialog billId={open} onClose={() => setOpen(null)} />}
    </>
  );
}
